import { capitalize } from 'lodash'
import { useCallback, useMemo, useState } from 'react'

import { usePSNavigate } from '../../hooks/usePSNavigate'
import { PSLink } from '../../models/PSLink'
import { RecordFactory } from '../../models/RecordFactory'
import { isValidUrl } from '../../utils'
import { BOOK_BASE } from '../../utils/constants'
import { ComponentData } from '../../utils/typing'
import { Icon } from '../Icon'
import { NavigationBlockerPrompt } from '../NavigationBlockerPrompt'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import Button from '../app/design/Button'
import Label from '../app/design/Label'
import Modal from '../app/design/Modal'
import { SearchableSelect } from '../common/fields/SearchableSelect'
import { TextField } from '../common/fields/TextField'
import { ArrangeableCustomOptions } from '../workflows/ArrangeableCustomOptions'
import { WorkflowPage } from '../workflows/WorkflowPage'
import { colorThemeOptionsWithUserCustomizations } from './Options'

const defaultLinks = [
    { title: 'Place an order', type: 'shop', featureID: 'widget', icon: 'package.large' },
    { title: 'Chat with me', type: 'chat', featureID: 'chat', icon: 'message.large' },
    { title: 'Sign up with me', type: 'lead', featureID: 'lead', icon: 'field.large' },
    { title: 'Send me a payment', type: 'cash', featureID: 'paypal', icon: 'dollar.large' },
    { title: 'Add a custom link', type: 'custom', featureID: 'booking', icon: 'listPlus.large' },
    { title: 'Book a session', type: 'book', featureID: 'widget', icon: 'calendar.large' },
] as const

type LinkType = (typeof defaultLinks)[number]['type']

type ModalInfo = {
    title: string
    body: string | JSX.Element
    button: string
    optionalButton?: string
    danger?: boolean
    onClick: () => void
}

type ModalState =
    | { type: 'closed' }
    | { type: 'select_link_type' }
    | { type: 'add_link'; link: PSLink }
    | { type: 'edit_link'; link: PSLink }

const viewWillAppear = async (base: PocketComponent) => {
    const linksResult = await RecordFactory.getInstance().fetchLinks()

    if (!linksResult.ok) {
        base.logAndDisplayError(linksResult.error)
        return
    }

    const [links, features] = await Promise.all([
        RecordFactory.getInstance().loadLinks(),
        RecordFactory.getInstance().loadFeatures(),
    ])

    return { links, features }
}

const render = ({ data, base }: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    const [user, setUser] = useState(base.me.clone())
    const navigate = usePSNavigate()

    const [suiteLinks, setSuiteLinks] = useState(
        data.links.sort((a, b) => (a.seqnum || 0) - (b.seqnum || 0))
    )

    const sortedLinksOptions = useMemo(() => {
        return suiteLinks.map((link) => ({
            id: link.id ?? '',
            title: link.label ?? '',
            seqnum: link.seqnum || 0,
            icon: link.icon,
            onDelete: () => onDeleteLink(link.id ?? ''),
            onClick: () => onEditLink(link),
            link: link,
        }))
    }, [suiteLinks])

    const [modalState, setModalState] = useState<ModalState>({ type: 'closed' })

    const [upgradeModalInfo, setUpgradeModalInfo] = useState<ModalInfo | null>(null)
    const [isSending, setIsSending] = useState(false)
    const [showCopySuccess, setShowCopySuccess] = useState(false)
    const [linksOrderChanged, setLinksOrderChanged] = useState(false)

    const isModalEditorAdd = modalState.type == 'edit_link' || modalState.type == 'add_link'

    const isLinkUrlInvalid = useMemo(() => {
        if (isModalEditorAdd && modalState.link?.url && modalState.link?.url.length > 0) {
            return !isValidUrl(modalState.link?.url)
        }
        return false
    }, [modalState])

    const [photo, setPhoto] = useState<
        { type: 'file'; file: File } | { type: 'url'; url: string } | null
    >(user.image?.url ? { type: 'url', url: user.image.url.toString() } : null)

    const colorItems = useMemo(() => {
        const colorOptions = colorThemeOptionsWithUserCustomizations(user)
        return colorOptions.map((option) => ({
            id: option.value,
            title: option.label,
            tertiaryView: (
                <div className="flex items-center gap-x-2">
                    <div
                        className="size-5 rounded-full"
                        style={{ backgroundColor: option.value }}
                    ></div>
                </div>
            ),
        }))
    }, [user])

    const suiteLinkURL = `${BOOK_BASE}/link/${user.username ?? user.key}`
    const hasEdits =
        base.me.primaryColor !== user.primaryColor ||
        base.me.secondaryColor !== user.secondaryColor ||
        photo?.type === 'file' ||
        linksOrderChanged

    // Only allow one of each default link except for custom links
    const addLinkOptions = useMemo(() => {
        return defaultLinks.filter(
            (d) => d.type === 'custom' || !Boolean(suiteLinks.find((l) => l.type === d.type))
        )
    }, [suiteLinks])

    const onEditLink = (link: PSLink) => {
        setModalState({ type: 'edit_link', link: link })
    }

    const onLinkTypeSelected = useCallback(
        (linkType: LinkType) => {
            // always allow custom links
            if (linkType === 'custom') {
                setModalState({ type: 'add_link', link: new PSLink({ type: linkType }) })
                return
            }

            if (!(user && user.industry && data.features.length)) return

            const linkInfo = defaultLinks.find((l) => l.type === linkType)
            if (!linkInfo) return

            const feature = data.features.find((f) => f.id === linkInfo.featureID)
            if (!feature) return

            if (!feature.allowed(user.industry) && feature.requiresUpgrade(user)) {
                setUpgradeModalInfo({
                    title: `${user.industry.title} Edition`,
                    body: (
                        <p>{`This feature requires upgrading to the PocketSuite ${user.industry.title} Edition. Select upgrade to view all of the benefits that come along with upgrading!`}</p>
                    ),
                    button: 'Upgrade',
                    onClick: () => {
                        navigate('/settings/change-plan')
                    },
                })
                return
            }

            if (!feature.allowed(user.industry)) {
                setUpgradeModalInfo({
                    title: feature.name ?? '',
                    body: <p>Would you like to unlock this feature?</p>,
                    button: 'Unlock feature',
                    onClick: async () => {
                        const clone = user.clone()
                        clone.allowFeatureByID(linkInfo.featureID)
                        base.showProgressBar('Saving...')
                        const result = await RecordFactory.getInstance().sendUser(clone)
                        base.hideProgressBar()

                        if (result.ok) {
                            setUser(clone)
                            base.reload()
                        }
                        else {
                            base.logAndDisplayError(result.error)
                        }
                    },
                })
                return
            }

            setModalState({ type: 'add_link', link: new PSLink({ type: linkType }) })
        },
        [user, data.features, setUpgradeModalInfo]
    )

    const onDragEnd = useCallback(
        async (
            source: (typeof sortedLinksOptions)[number],
            destination: (typeof sortedLinksOptions)[number],
            newCustomOptions: typeof sortedLinksOptions
        ) => {
            if (!destination) return

            setLinksOrderChanged(true)

            setIsSending(true)
            const movedResult = await RecordFactory.getInstance().moveLink(
                source.link,
                destination.seqnum
            )
            setIsSending(false)

            if (!movedResult.ok) {
                base.logAndDisplayError(movedResult.error)
                // reload in case of error so we get the page back to the correct list order
                base.reload()
                return
            }

            setSuiteLinks(newCustomOptions.map((option) => option.link))
        },
        [setSuiteLinks]
    )

    const handleSave = async () => {
        base.showProgressBar('Saving...')
        setLinksOrderChanged(false)

        const result = await RecordFactory.getInstance().sendUser(
            user,
            photo?.type === 'file' ? photo.file : undefined
        )
        base.hideProgressBar()
        if (!result.ok) {
            base.logAndDisplayError(result.error)
            return
        }

        navigate('/settings')
    }

    const handleSaveLink = async () => {
        if (!isModalEditorAdd || modalState.link === undefined) {
            return
        }

        base.showProgressBar()
        const result = await RecordFactory.getInstance().sendLink(modalState.link)
        base.hideProgressBar()

        if (!result.ok) {
            base.logAndDisplayError(result.error)
            return
        }
        if (!(result.value instanceof PSLink)) return
        const savedLink = result.value

        setSuiteLinks((prevLinks) => {
            const linkIndex = prevLinks.findIndex((item) => item.id === savedLink.id)

            if (linkIndex !== -1) {
                return prevLinks.map((link, index) => (index === linkIndex ? savedLink : link))
            }

            return [...prevLinks, savedLink]
        })
        setModalState({ type: 'closed' })
    }

    const onDeleteLink = async (linkId: string) => {
        base.showProgressBar()
        const result = await RecordFactory.getInstance().deleteLink(linkId)
        base.hideProgressBar()

        if (!result.ok) {
            base.logAndDisplayError(result.error)
            return
        }
        setSuiteLinks((suiteLinks) => suiteLinks.filter((link) => link.id !== linkId))
    }

    const getModalTitle = () => {
        switch (modalState.type) {
            case 'select_link_type':
                return 'Select link'
            case 'add_link':
                return (
                    defaultLinks.find((link) => link.type === modalState.link?.type)?.title ||
                    'Add custom link'
                )
            case 'edit_link':
                return (
                    defaultLinks.find((link) => link.type === modalState.link?.type)?.title ||
                    'Edit custom link'
                )
        }
    }

    const handleSaveAndPreview = async () => {
        await handleSave()
        window.open(suiteLinkURL, '_blank')
    }

    return (
        <WorkflowPage
            title="Your Suite Link"
            footerContent={
                <div className="flex w-full items-center justify-end gap-x-4">
                    <Button intent="secondary" onClick={handleSaveAndPreview}>
                        Save & Preview
                    </Button>
                    <Button intent="primary" onClick={handleSave} disabled={isSending || !hasEdits}>
                        Save
                    </Button>
                </div>
            }
        >
            <div className="mt-12 flex w-full flex-col items-center justify-start gap-y-6">
                <WorkflowPage.Card title="General">
                    <div className="flex w-full flex-col items-center">
                        <WorkflowPage.PhotoPicker
                            onSelectPhoto={(photoFile) =>
                                setPhoto({ type: 'file', file: photoFile })
                            }
                            selectedPhoto={photo}
                            onClearPhoto={() => setPhoto(null)}
                        />
                    </div>

                    <WorkflowPage.IconSection
                        title="Suite link"
                        icon="monitor.large"
                        thirdLine={
                            <div className="mt-2 flex w-full items-center justify-between rounded-lg bg-gray-100 p-[5px] pl-4">
                                <span className="text-base font-normal">{suiteLinkURL}</span>
                                <Button
                                    intent="secondary"
                                    size="small"
                                    onClick={() => {
                                        navigator.clipboard
                                            .writeText(suiteLinkURL)
                                            .then(() => setShowCopySuccess(true))
                                    }}
                                >
                                    Copy
                                </Button>
                            </div>
                        }
                    />

                    <WorkflowPage.IconSection
                        title="Primary color"
                        icon="palette.large"
                        description="Choose your widget colors here or send us your hex codes for more customization."
                    >
                        <SearchableSelect
                            size="medium"
                            className="w-full border-gray-500"
                            items={colorItems}
                            onSelectItem={(item) =>
                                setUser(user.clone({ primary_color: item?.id }))
                            }
                            selectedItemID={user.primaryColor}
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection title="Secondary color" icon="palette.large">
                        <SearchableSelect
                            size="medium"
                            className="w-full border-gray-500"
                            items={colorItems}
                            onSelectItem={(item) =>
                                setUser(user.clone({ secondary_color: item?.id }))
                            }
                            selectedItemID={user.secondaryColor}
                        />
                    </WorkflowPage.IconSection>
                </WorkflowPage.Card>

                <WorkflowPage.Card title="Links">
                    <WorkflowPage.IconSection
                        title="Your Links"
                        icon="monitor.large"
                        description="Drag to reorder links as they appear on your Suite Link page"
                        hasHoverEffect
                        secondaryView={
                            data.links.length < PSLink.SUITELINK_LIMIT ? (
                                <button
                                    className="link text-primary-700 text-[14px] underline"
                                    onClick={() => setModalState({ type: 'select_link_type' })}
                                >
                                    Add
                                </button>
                            ) : (
                                <></>
                            )
                        }
                    >
                        <ArrangeableCustomOptions
                            options={sortedLinksOptions}
                            onDragEnd={onDragEnd}
                            shouldAlignDeleteButtonWithContainingCard
                        />
                    </WorkflowPage.IconSection>
                </WorkflowPage.Card>
            </div>

            <Modal
                isOpen={showCopySuccess}
                title="Suite link copied!"
                onClose={() => setShowCopySuccess(false)}
                footer={
                    <div className="flex w-full items-center justify-end gap-x-4">
                        <Button intent="primary" onClick={() => setShowCopySuccess(false)}>
                            Close
                        </Button>
                    </div>
                }
            >
                Your suite link has been copied to your clipboard! Paste it to share with a client
                or on social media.
            </Modal>

            <Modal
                isOpen={modalState.type === 'select_link_type'}
                onClose={() => setModalState({ type: 'closed' })}
                title="Select link"
            >
                <div className="flex flex-col gap-4">
                    {addLinkOptions.map((o) => (
                        <button
                            onClick={() => onLinkTypeSelected(o.type)}
                            className="flex cursor-pointer items-center justify-between rounded-lg border border-gray-300 bg-white p-4 hover:bg-black/4 active:bg-black/8"
                        >
                            <div className="flex items-center gap-x-4">
                                <Icon icon={o.icon} background="round" className="shrink-0" />
                                <div className="font-bold">{o.title}</div>
                            </div>
                            <Icon className="text-gray-500" icon="chevronRight.large" />
                        </button>
                    ))}
                </div>
            </Modal>

            <Modal
                isOpen={isModalEditorAdd}
                onClose={() => setModalState({ type: 'closed' })}
                title={getModalTitle()}
                onNavigateBack={
                    modalState.type === 'add_link'
                        ? () => setModalState({ type: 'select_link_type' })
                        : undefined
                }
                footer={
                    <div className="flex justify-end">
                        <Button
                            intent="primary"
                            disabled={
                                isModalEditorAdd &&
                                (!modalState.link?.label ||
                                    (!modalState.link?.isDefaultSuiteLink && !modalState.link?.url))
                            }
                            onClick={handleSaveLink}
                        >
                            Save
                        </Button>
                    </div>
                }
            >
                <div className="flex flex-col items-stretch justify-center gap-y-8">
                    <WorkflowPage.IconSection icon="info.large" title="Label (required)">
                        <TextField
                            size="medium"
                            placeholder="Enter text"
                            value={isModalEditorAdd ? modalState.link?.label : ''}
                            onChange={(event) => {
                                if (isModalEditorAdd) {
                                    const updatedLink = modalState.link.clone({
                                        label: event.target.value,
                                    })
                                    setModalState({
                                        ...modalState,
                                        link: updatedLink,
                                    })
                                }
                            }}
                            onClear={() => {
                                if (isModalEditorAdd) {
                                    const updatedLink = modalState.link.clone({
                                        label: '',
                                    })
                                    setModalState({
                                        ...modalState,
                                        link: updatedLink,
                                    })
                                }
                            }}
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection
                        icon="link.large"
                        title={
                            isModalEditorAdd
                                ? modalState.link?.isDefaultSuiteLink
                                    ? 'Link (Generated by PocketSuite)'
                                    : 'Link (Required)'
                                : ''
                        }
                        disabled={isModalEditorAdd && modalState.link?.isDefaultSuiteLink}
                    >
                        <TextField
                            size="medium"
                            placeholder="Enter text"
                            className={
                                isModalEditorAdd && modalState.link?.isDefaultSuiteLink
                                    ? 'text-gray-400'
                                    : ''
                            }
                            disabled={isModalEditorAdd && modalState.link?.isDefaultSuiteLink}
                            value={
                                isModalEditorAdd
                                    ? modalState.link?.isDefaultSuiteLink
                                        ? capitalize(modalState.link?.type)
                                        : modalState.link?.url
                                    : ''
                            }
                            onChange={(event) => {
                                if (isModalEditorAdd) {
                                    setModalState({
                                        ...modalState,
                                        link: modalState.link.clone({ url: event.target.value }),
                                    })
                                }
                            }}
                            onClear={() => {
                                if (isModalEditorAdd) {
                                    setModalState({
                                        ...modalState,
                                        link: modalState.link.clone({ url: '' }),
                                    })
                                }
                            }}
                            hasError={isLinkUrlInvalid}
                        />
                        {isLinkUrlInvalid && (
                            <Label size="xs" intent="danger">
                                That does not appear to be a valid website address.
                            </Label>
                        )}
                    </WorkflowPage.IconSection>
                </div>
            </Modal>

            <Modal
                isOpen={!!upgradeModalInfo}
                onClose={() => setUpgradeModalInfo(null)}
                title={upgradeModalInfo?.title}
                onNavigateBack={() => setUpgradeModalInfo(null)}
                footer={
                    <div className="flex justify-end">
                        <Button intent="primary" onClick={upgradeModalInfo?.onClick}>
                            {upgradeModalInfo?.button}
                        </Button>
                    </div>
                }
            >
                {upgradeModalInfo?.body}
            </Modal>

            <NavigationBlockerPrompt when={hasEdits} />
        </WorkflowPage>
    )
}

export const SetupSuiteLinkPage = usePSComponent(render, viewWillAppear)
